import classNames from 'classnames';
import React from 'react';
import * as styles from './PostIcon.module.scss';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import SVGIcon from '../components/SVGIcon';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostType } from '../graphql/generated/graphql';
import draftIcon from '../icons/non-prefilled/16x16/drafts.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import templateIcon from '../icons/non-prefilled/16x16/template.svg';
import prefilledDraftIcon from '../icons/prefilled/16x16/drafts.svg';
import prefilledPostIcon from '../icons/prefilled/16x16/post.svg';
import postArchivedIcon from '../icons/prefilled/16x16/postArchived.svg';
import templateFilledIcon from '../icons/prefilled/16x16/template.svg';
import templateBaseIcon from '../icons/prefilled/16x16/templateBase.svg';
import templateDraftIcon from '../icons/prefilled/16x16/templateDraft.svg';
import getExternalBrandLogoProps from '../integrations/getExternalBrandLogoProps';

const POST_ICON_POST_FRAGMENT = gql(`
  fragment PostIcon_post on RootPostFields {
    id
    ... on SlimPostFields {
      archivedAt
      publishedAt
      externalPost {
        id
        platform
      }
      type
    }
  }
`);

type PostFragment = DocumentType<typeof POST_ICON_POST_FRAGMENT>;

type PostState = {
  type: PostType;
  draft: boolean;
  archived: boolean;
};

export const getStateIcon = (
  { type, draft, archived }: PostState,
  prefilled = false,
) => {
  switch (type) {
    case PostType.Post: {
      if (draft) return prefilled ? prefilledDraftIcon : draftIcon;
      if (archived) return prefilled ? postArchivedIcon : postIcon;
      return prefilled ? prefilledPostIcon : postIcon;
    }
    case PostType.Template: {
      if (draft) return templateDraftIcon;
      if (archived) return prefilled ? templateBaseIcon : templateIcon;
      return prefilled ? templateFilledIcon : templateIcon;
    }
    default: {
      return prefilled ? prefilledPostIcon : postIcon;
    }
  }
};

const getStateText = (state: PostState) => {
  const entity = state.type.toLowerCase();
  if (state.draft) return `draft ${entity}`;
  if (state.archived) return `archived ${entity}`;
  return entity;
};

const GET_POST_STATE_POST_FRAGMENT = gql(`
  fragment getPostState_post on RootPostFields {
    id
    ... on SlimPostFields {
      archivedAt
      publishedAt
      type
    }
  }
`);

export const getPostState = (
  post?: DocumentType<typeof GET_POST_STATE_POST_FRAGMENT> | null,
): PostState => {
  if (post) {
    const type = 'type' in post ? post.type : PostType.Post;
    const isDraft = 'publishedAt' in post ? post.publishedAt == null : false;
    const isArchived = 'archivedAt' in post ? post.archivedAt != null : false;

    return {
      type,
      draft: isDraft,
      archived: isArchived,
    };
  }

  return { type: PostType.Post, draft: false, archived: false };
};

const GET_POST_ICON_POST_FRAGMENT = gql(`
  fragment getPostIcon_post on RootPostFields {
    id
    ...getPostState_post
  }
`);

export const getPostIcon = (
  post: DocumentType<typeof GET_POST_ICON_POST_FRAGMENT>,
  prefilled?: boolean,
) => getStateIcon(getPostState(post), prefilled);

interface PostIconForStateProps {
  state: PostState;
  externalIcon?: React.ReactNode;
  prefilled?: boolean;
  bordered?: boolean;
}

export const PostIconForState = ({
  state,
  externalIcon,
  prefilled,
  bordered,
}: PostIconForStateProps) => {
  return (
    <div
      className={classNames(
        styles.container,
        styles[state.type.toLowerCase()],
        {
          [styles.archived]: state.archived,
          [styles.draft]: state.draft,
          [styles.bordered]: bordered,
        },
      )}
      role="img"
      aria-label={getStateText(state)}
    >
      <SVGIcon>{getStateIcon(state, prefilled)}</SVGIcon>
      {!!externalIcon && (
        <div className={styles.externalIconContainer}>
          <div className={styles.externalIconInner}>{externalIcon}</div>
        </div>
      )}
    </div>
  );
};

interface PostIconProps {
  post?: PostFragment;
  bordered?: boolean;
}

const PostIcon = ({ post, bordered }: PostIconProps) => {
  const isDraft =
    post && 'publishedAt' in post ? post.publishedAt == null : false;

  const postState = getPostState(post);

  const externalIcon = post && 'externalPost' in post && post.externalPost && (
    <ExternalBrandLogo
      {...getExternalBrandLogoProps(post.externalPost.platform)}
      height={8}
    />
  );

  return (
    <PostIconForState
      state={postState}
      externalIcon={externalIcon || undefined}
      prefilled={!isDraft}
      bordered={bordered}
    />
  );
};

export default PostIcon;
